<template>
    <div class="main-container px-5 py-5 xl:px-16">
        <Toast />

        <div class="flex flex-col pt-8">
            <!-- Add search input -->
            <div class="mb-4 flex flex-col md:flex-row gap-4 items-start md:items-center">
                <div class="w-full md:w-1/3">
                    <span class="p-input-icon-left w-full">
                        <InputText 
                            v-model="searchQuery" 
                            placeholder="Search by name..." 
                            class="w-full p-inputtext-sm"
                            ref="searchInput"
                             :class="{ 'search-highlight': searchHighlight }"
                        />
                    </span>
                </div>
                <div class="flex items-center gap-2">
                    <span class="text-sm">Hide blocked/rejected users</span>
                    <ToggleSwitch v-model="hideBlockedUsers">
                        <template #handle="{ checked }">
                            <i :class="['!text-xs pi', { 'pi-check': checked, 'pi-times': !checked }]" />
                        </template>
                    </ToggleSwitch>
                </div>
                <Button
                    :label="showActiveWallets ? 'Show All' : 'Active Wallets'"
                    :icon="showActiveWallets ? 'pi pi-list' : 'pi pi-wallet'"
                    :severity="showActiveWallets ? 'secondary' : 'primary'"
                    size="small"
                    text
                    @click="showActiveWallets = !showActiveWallets"
                />
            </div>

            <div class="w-full border-1 card-bg-dark rounded-lg shadow-md p-2">
                <DataTable v-if="users.length" :editingRows="editingRows" @cell-edit-complete="onCellEditComplete"
                    :value="filteredUsers" editMode="cell" dataKey="id" class="custom-data-table"
                    :scrollHeight="'70vh'"
                    scrollable
                >
                    <Column field="name" header="Name" :headerStyle="headerStyles" >
                        <template #body="slotProps">
                            <!-- Create a router link using the data in the slotProps -->
                            <router-link v-if="!isSubAffiliate" :to="`/users/${slotProps.data.id}`" class="clickable-link">
                                {{ slotProps.data.name }}
                            </router-link>
                            <span v-else>{{ slotProps.data.name }}</span>
                        </template>
                    </Column>
                    <Column field="role" header="Type" :headerStyle="headerStyles">
                        <template #body="slotProps">
                            <component :is="getRoleTag(slotProps.data.roles)" />
                        </template>
                    </Column>
                    <Column field="email" header="Email" :headerStyle="headerStyles"></Column>
                    <Column field="country" header="Country" :headerStyle="headerStyles">
                        <template #body="slotProps">
                            <!-- <span>{{ getCountryName(slotProps.data.country) }}</span> -->
                            <span :class="`fi fi-${slotProps.data.country.toLowerCase()}`" class="ml-2"></span>
                        </template>
                    </Column>
                    <Column field="lastLogin" header="Last Logged in" :headerStyle="headerStyles">
                        <template #body="slotProps">
                        <div>{{ getFormattedDate(slotProps.data.lastLogin) }}</div>
                    </template>
                    </Column>
                    <Column field="created" header="Date registered" :headerStyle="headerStyles"></Column>

                    <Column field="walletBalance" header="Wallet" :headerStyle="headerStyles">
                        <template #body="{ data }">
                            <span :class="data.walletBalance > 0 ? 'text-green-400' : 'text-gray-400'" class="font-semibold">
                                {{ formatCurrency(data.walletBalance || 0) }}
                            </span>
                        </template>
                    </Column>

                    <Column field="status" header="Status" style="width: 20%" :headerStyle="headerStyles">
                        <!-- <template #body="slotProps">
                        <Tag :value="slotProps.data.status" :severity="getSeverity(slotProps.data)" /> -->
                        <template v-if="!isSubAffiliate" #editor="{ data, field }">
                            <Select v-model="data[field]" :options="statuses" optionLabel="label" optionValue="status"
                                placeholder="Select a Status" fluid>
                                <template #option="slotProps">
                                    <Tag :value="slotProps.option.status"
                                        :severity="getSeverity(slotProps.option.status)" />
                                </template>
                            </Select>
                        </template>
                        <template #body="slotProps">
                            <Tag :value="slotProps.data.status" :severity="getSeverity(slotProps.data.status)"
                                class="cursor-pointer" />
                        </template>
                        <!-- </template> -->
                    </Column>
                </DataTable>
                <div v-else class="flex justify-center items-center w-full h-full py-5">
                    <Skeleton width="90%" height="50vh"></Skeleton>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, toRaw, computed, onUnmounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { getFormattedDate } from '@/utils/dateHelper';
import UsersService from '@/services/UsersService';
import { useToast } from "primevue/usetoast";
import { getCountryName } from '@/utils/countries';
import { useStore } from 'vuex';
import { formatCurrency } from '@/utils/currencies';

const route = useRoute();
const router = useRouter();
const store = useStore();
const toast = useToast();
const users = ref([]);
const editingRows = ref([]);
import { headerStyles } from '@/global/headerStyles';
import { getRoleTag } from '@/utils/roles';
const isLoading = ref(true);
const isSubAffiliate = computed(() => store.getters.isSubAffiliate);
const statuses = ref([
    { label: 'Pending KYC', status: 'pending kyc' },
    { label: 'Approved', status: 'approved' },
    { label: 'Rejected', status: 'rejected' },
    { label: 'Bocked', status: 'blocked' },
]);

const getSeverity = (status) => {
    switch (status) {
        case 'pending kyc':
            return 'secondary';
        case 'approved':
            return 'success';
        case 'rejected':
            return 'danger';
        case 'blocked':
            return 'contrast'
        default:
            return 'danger';
    }
};

const onCellEditComplete = async (event) => {
    let { newData } = event;
    let oldUser = toRaw(event.data);
    let index = users.value.findIndex(user => user.id === newData.id);
    if (newData.status !== oldUser.status) {
        users.value[index] = newData;
        try {
            await UsersService.patch(newData)
        } catch (err) {
            toast.add({ severity: 'error', summary: 'Update failed', detail: 'Please refresh and try again', life: 3000 });
            console.error(err)
            users.value[index] = oldUser;
        }
    }
}

async function getUsers(){
    await UsersService.getAll().then((data) => {
        console.log(data);
        users.value = data.data;
    });
}

// Add search functionality
const searchQuery = ref('');
const hideBlockedUsers = ref(true);
const showActiveWallets = ref(route.query.activeWallets === 'true');

watch(showActiveWallets, (val) => {
    const query = { ...route.query };
    if (val) query.activeWallets = 'true';
    else delete query.activeWallets;
    router.replace({ query });
});

// Add computed property for filtered users
const filteredUsers = computed(() => {
    let filtered = users.value;

    // Filter by search query
    if (searchQuery.value) {
        filtered = filtered.filter(user =>
            user.name.toLowerCase().includes(searchQuery.value.toLowerCase())
        );
    }

    // Filter out blocked/rejected users if hideBlockedUsers is true
    if (hideBlockedUsers.value) {
        filtered = filtered.filter(user =>
            user.status !== 'blocked' && user.status !== 'rejected'
        );
    }

    // Active wallets — only users with balance > 0, sorted highest first
    if (showActiveWallets.value) {
        filtered = filtered
            .filter(user => (user.walletBalance || 0) > 0)
            .sort((a, b) => b.walletBalance - a.walletBalance);
        return filtered;
    }

    // Default: approved users first
    return filtered.sort((a, b) => {
        if (a.status === 'approved' && b.status !== 'approved') return -1;
        if (a.status !== 'approved' && b.status === 'approved') return 1;
        return 0;
    });
});

// Add these refs
const searchInput = ref(null);
const searchHighlight = ref(false);

// Modify the keydown handler
function handleKeyDown(event) {
    if ((event.ctrlKey || event.metaKey) && event.key === 'f') {
        event.preventDefault();
        
        // Access the actual DOM element
        if (searchInput.value && searchInput.value.$el) {
            searchInput.value.$el.focus();

            // Trigger highlight effect
            searchHighlight.value = true;

            // Remove highlight after animation completes
            setTimeout(() => {
                searchHighlight.value = false;
            }, 1000); // 1 second duration
        }
    }
}


onMounted(async () => {
   await getUsers();
   isLoading.value = false
   document.addEventListener('keydown', handleKeyDown);
});

onUnmounted(() => {
    document.removeEventListener('keydown', handleKeyDown);
});


</script>

<style scoped>
.custom-data-table .p-datatable-thead>tr>th {
    background-color: #f0f4f8;
    color: #333;
    border-bottom: 1px solid #ccc;
}

.clickable-link {
    text-decoration: underline;
    color: inherit;
    /* Inherit color from the parent element */
    cursor: pointer;
}

.clickable-link:hover {
    color: white;
    /* Change the color on hover */
}

@keyframes pulse-border {
    0% {
        box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.7);
    }

    70% {
        box-shadow: 0 0 0 25px rgba(59, 130, 246, 0);
    }

    100% {
        box-shadow: 0 0 0 0 rgba(59, 130, 246, 0);
    }
}

.search-highlight {
    animation: pulse-border 1s ease-out;
    border-color: #3b82f6;
    /* blue-500 */
    background-color: rgba(219, 234, 254, 0.5);
    /* blue-100 with transparency */
}
</style>